import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { z } from 'zod';
import { graphql } from '~/apollo/generated/v4';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { useZodForm } from '~/utils/forms';
import { VftSlideFormFields } from './vft-slide-form-fields';
import type { VftSlideFormValues } from '~/utils/modules/vft';
import { vftSlideSchema } from '~/utils/modules/vft';
import type { ReactNode } from 'react';
import { useModalState } from '~/hooks/modal';
import { Modal } from '~/components/common/Modal';
import type { SlideType } from '~/routes/upload/vft-manager/$vftId';

const UPDATE_VFT_SLIDE = graphql(`
  mutation UpdateVftSlide($id: ID!, $input: TourSlideUpdateInput!) {
    tourSlideUpdate(id: $id, input: $input) {
      result {
        id
        lifecycleStatus
        position
        text
        title
      }
    }
  }
`);

function initialVftSlide(vftSlide: SlideType): VftSlideFormValues {
  return {
    title: vftSlide.title,
    text: vftSlide.text,
  };
}

export function UpdateVftSlideModal({
  vftSlide,
  successCallback,
  children,
}: {
  vftSlide: SlideType;
  successCallback?: (id: string) => void;
  children: (showModal: () => void) => ReactNode;
}) {
  const { show, showModal, hideModal } = useModalState();
  const [updateVftSlide, { loading, error }] = useMutation(UPDATE_VFT_SLIDE);

  const form = useZodForm({
    schema: vftSlideSchema,
    values: initialVftSlide(vftSlide),
  });

  const handleSubmit = form.handleSubmit(
    async (values: z.infer<typeof vftSlideSchema>) => {
      const { ...input } = values;
      try {
        const res = await updateVftSlide({
          variables: {
            id: vftSlide.id,
            input: {
              ...input,
            },
          },
        });
        const id = res.data?.tourSlideUpdate?.result?.id;
        if (!id) {
          toast.error('Updating slide failed');
          return;
        }
        toast.success('Slide Updated');
        form.reset();
        hideModal();
        if (successCallback) {
          successCallback(id);
        }
      } catch (err) {
        console.log('Error saving slide', err);
        toast.error('There was a problem saving the slide.');
      }
    },
  );

  return (
    <>
      {children(showModal)}
      <Modal open={show} onHide={hideModal}>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <VftSlideFormFields create={false} />
            <HookFormErrors graphQLError={error} />

            <div className="text-center">
              <Button type="submit" color="primary" disabled={loading}>
                Save
              </Button>
            </div>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
}
